import React, { useEffect, useCallback, useRef, useState } from "react";
import { useOutlookStore } from "../store/outlookStore";
import { usePlatform, WebAddinPlatforms } from "./usePlatform";
import { useOutlookActions } from "./useOutlookActions";

declare const Office: any;

export const useOutlookAppointmentEvents = () => {
  console.log("🎯 useOutlookAppointmentEvents hook initialized");

  const {
    setAppointmentTime,
    setSubject,
    setLocation,
    setEnhancedLocation,
    setOrganizer,
    setError,
    setRequiredAttendees,
    setOptionalAttendees,
    getAll: getAppointmentStoreData,
    initializeAppointmentTime,
  } = useOutlookStore();

  const { getCurrentPlatform, isSetSupported } = usePlatform();
  const { InitializeAppointmentData } = useOutlookActions();

  // State management
  const isSubscribed = useRef(false);
  const [eventStatus, setEventStatus] = useState({
    recipients: false,
    timeChange: false,
    enhancedLocations: false,
    recurrence: false,
  });

  // Debouncing refs
  const recipientsDebounceRef = useRef<NodeJS.Timeout>();
  const timeChangeDebounceRef = useRef<NodeJS.Timeout>();

  const loadSession = useCallback((callbacks: any) => {
    try {
      let item = Office.context.mailbox.item;
      if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
        let appCompose = item as Office.AppointmentCompose;
        if (appCompose) {
          appCompose.sessionData.getAllAsync(callbacks);
        }
      }
    } catch (error) {
      console.error("Failed to load session:", error);
    }
  }, []);

  const loadSessionCallback = useCallback((asyncResult: Office.AsyncResult<Office.SessionData>) => {
    if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
      console.error("Failed to load session data:", asyncResult.error);
    }
  }, []);

  // Utility function for debouncing
  const debounce = useCallback(
    (func: Function, delay: number, timeoutRef: React.MutableRefObject<NodeJS.Timeout | undefined>) => {
      return (...args: any[]) => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => func(...args), delay);
      };
    },
    []
  );

  // Time change handler with debouncing - proper event handler signature
  const handleAppointmentTimeChangeInternal = useCallback(
    (eventArgs: Office.AppointmentTimeChangedEventArgs) => {
      try {
        console.log("⏰ TIME CHANGED EVENT FIRED!", eventArgs);
        // The event args already contain the time changes
        if (eventArgs && eventArgs.start && eventArgs.end) {
          console.log("✅ Setting appointment time:", { start: eventArgs.start, end: eventArgs.end });
          setAppointmentTime(eventArgs.start, eventArgs.end);
          setEventStatus((prev) => ({ ...prev, timeChange: true }));
        } else {
          // Fallback: get the current times from the appointment
          const item = Office.context.mailbox.item;
          if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
            const appCompose = item as Office.AppointmentCompose;
            if (appCompose) {
              Promise.all([
                new Promise<Date>((resolve, reject) => {
                  appCompose.start.getAsync((result: Office.AsyncResult<Date>) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                      resolve(result.value);
                    } else {
                      reject(new Error(`Failed to get start time: ${result.error?.message}`));
                    }
                  });
                }),
                new Promise<Date>((resolve, reject) => {
                  appCompose.end.getAsync((result: Office.AsyncResult<Date>) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                      resolve(result.value);
                    } else {
                      reject(new Error(`Failed to get end time: ${result.error?.message}`));
                    }
                  });
                }),
              ])
                .then(([startTime, endTime]) => {
                  setAppointmentTime(startTime, endTime);
                  setEventStatus((prev) => ({ ...prev, timeChange: true }));
                })
                .catch((error) => {
                  console.error("Failed to get appointment times:", error);
                });
            }
          }
        }
      } catch (error) {
        console.error("Error in appointment time change handler:", error);
      }
    },
    [setAppointmentTime, setEventStatus]
  );

  // Debounced time change handler
  const handleAppointmentTimeChange = useCallback(
    debounce(handleAppointmentTimeChangeInternal, 500, timeChangeDebounceRef),
    [handleAppointmentTimeChangeInternal, debounce]
  );

  // Enhanced locations change handler
  const handleEnhancedLocationsChange = useCallback(
    (asyncResult: Office.EnhancedLocationsChangedEventArgs) => {
      // debugger;
      try {
        console.log("📍 ENHANCED LOCATIONS CHANGED EVENT FIRED!", asyncResult);
        const currentPlatform = getCurrentPlatform();
        const isMailbox18Supported = isSetSupported("Mailbox", "1.8");
        const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

        // Only handle enhanced locations for supported platforms
        if (!isMailbox18Supported || isPlatformMac) {
          return;
        }

        const locations = asyncResult?.enhancedLocations || [];
        console.log("📍 Enhanced locations data:", locations);

        setEnhancedLocation(locations);
      } catch (error) {
        console.error("Error in enhanced locations change handler:", error);
      }
    },
    [getCurrentPlatform, isSetSupported, setEnhancedLocation]
  );

  // Recipients change handler internal logic
  const handleRecipientsChangeInternal = useCallback(
    (eventArgs: Office.RecipientsChangedEventArgs) => {
      try {
        console.log("👥 RECIPIENTS CHANGED EVENT FIRED!", eventArgs);
        const organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
        console.log("📧 Setting organizer:", organizerEmail);

        // Set organizer in AppointmentStore
        setOrganizer(organizerEmail);

        // Get all current recipients from the appointment item
        const item = Office.context.mailbox.item;
        if (item && item.itemType === Office.MailboxEnums.ItemType.Appointment) {
          const appCompose = item as Office.AppointmentCompose;

          // Process required attendees
          appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const recipients = (result.value || []).filter(
                (recipient) => recipient.emailAddress && recipient.emailAddress.toLowerCase() !== organizerEmail
              );

              // Convert to OutlookRecipient format for AppointmentStore
              const requiredAttendees = recipients.map((r) => ({
                Name: r.displayName || r.emailAddress || "",
                Email: r.emailAddress || "",
                RecipientType: r.recipientType == "user" ? 0 : 2,
              }));
              console.log("✅ Setting required attendees:", requiredAttendees);
              setRequiredAttendees(requiredAttendees);
              setEventStatus((prev) => ({ ...prev, recipients: true }));
            }
          });

          // Process optional attendees
          appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const recipients = (result.value || []).filter(
                (recipient) => recipient.emailAddress && recipient.emailAddress.toLowerCase() !== organizerEmail
              );

              // Convert to OutlookRecipient format for AppointmentStore
              const optionalAttendees = recipients.map((r) => ({
                Name: r.displayName || r.emailAddress || "",
                Email: r.emailAddress || "",
                RecipientType: r.recipientType == "user" ? 0 : 2,
              }));
              console.log("✅ Setting optional attendees:", optionalAttendees);
              setOptionalAttendees(optionalAttendees);
            }
          });
        }
      } catch (error) {
        console.error("Error in recipients change handler:", error);
      }
    },
    [setRequiredAttendees, setOptionalAttendees, setEventStatus, setOrganizer]
  );

  // Debounced recipients change handler
  const handleRecipientsChange = useCallback(debounce(handleRecipientsChangeInternal, 300, recipientsDebounceRef), [
    handleRecipientsChangeInternal,
    debounce,
  ]);

  // Recurrence change handler
  const handleRecurrenceChange = useCallback((asyncResult: Office.RecurrenceChangedEventArgs) => {
    try {
      console.log("Recurrence changed:", asyncResult);
      // Add your recurrence change logic here
    } catch (error) {
      console.error("Error in recurrence change handler:", error);
    }
  }, []);

  // Note: Subject change events are not available for appointment compose items
  // They are only available in read scenarios, not compose scenarios

  // Subscribe to recipients changes using Office.js events
  const subscribeToRecipientsChange = useCallback(() => {
    try {
      console.log("🔥 Subscribing to recipients changes...");

      // Try to subscribe to recipients changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.RecipientsChanged,
        handleRecipientsChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("✅ Recipients change subscription successful");
            setEventStatus((prev) => ({ ...prev, recipients: true }));
          } else {
            console.error("❌ Recipients change subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in recipients subscription:", error);
    }
  }, [handleRecipientsChange, setEventStatus]);

  // Subscribe to time changes using Office.js events
  const subscribeToTimeChange = useCallback(() => {
    try {
      console.log("🔥 Subscribing to time changes...");

      // Try to subscribe to appointment time changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.AppointmentTimeChanged,
        handleAppointmentTimeChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("✅ Time change subscription successful");
            setEventStatus((prev) => ({ ...prev, timeChange: true }));
          } else {
            console.error("❌ Time change subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in time change subscription:", error);
    }
  }, [handleAppointmentTimeChange, setEventStatus]);

  // Subscribe to enhanced locations changes using Office.js events
  const subscribeToEnhancedLocations = useCallback(() => {
    try {
      console.log("🔥 Subscribing to enhanced locations...");

      // Try to subscribe to enhanced locations changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.EnhancedLocationsChanged,
        handleEnhancedLocationsChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("✅ Enhanced locations subscription successful");
            setEventStatus((prev) => ({ ...prev, enhancedLocations: true }));
          } else {
            console.error("❌ Enhanced locations subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in enhanced locations subscription:", error);
    }
  }, [handleEnhancedLocationsChange, setEventStatus]);

  // Subscribe to recurrence changes using Office.js events
  const subscribeToRecurrenceChange = useCallback(() => {
    try {
      console.log("🔥 Subscribing to recurrence changes...");

      // Try to subscribe to recurrence changed event
      Office.context.mailbox.item.addHandlerAsync(
        Office.EventType.RecurrenceChanged,
        handleRecurrenceChange,
        (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("✅ Recurrence change subscription successful");
            setEventStatus((prev) => ({ ...prev, recurrence: true }));
          } else {
            console.error("❌ Recurrence change subscription failed:", result.error);
          }
        }
      );
    } catch (error) {
      console.error("❌ Exception in recurrence subscription:", error);
    }
  }, [handleRecurrenceChange, setEventStatus]);

  // Initialize current data from Outlook
  const initializeCurrentData = useCallback(async () => {
    try {
      console.log("🔄 Initializing current appointment data...");

      // Use the comprehensive InitializeAppointmentData function
      const success = await InitializeAppointmentData();
      if (success) {
        console.log("✅ Appointment data initialized successfully");

        // Also trigger recipients load to ensure everything is in sync
        handleRecipientsChangeInternal({} as Office.RecipientsChangedEventArgs);
      } else {
        console.warn("⚠️ Failed to initialize appointment data, using fallback...");

        // Fallback: try to initialize basic appointment time
        await initializeAppointmentTime();

        // Trigger initial recipients load
        handleRecipientsChangeInternal({} as Office.RecipientsChangedEventArgs);
      }
    } catch (error) {
      console.error("❌ Error initializing current data:", error);

      // Final fallback: try store's initializeAppointmentTime
      try {
        await initializeAppointmentTime();
      } catch (fallbackError) {
        console.error("❌ Fallback initialization also failed:", fallbackError);
      }
    }
  }, [InitializeAppointmentData, initializeAppointmentTime, handleRecipientsChangeInternal]);

  const initializeEventSubscriptions = useCallback(async () => {
    console.log("🚀 Initializing event subscriptions...");

    // Check Office.js availability
    if (!Office || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
      console.error("❌ Office.js context not available!");
      return;
    }

    const item = Office.context.mailbox.item;
    console.log("📧 Item type:", item?.itemType, "Expected:", Office.MailboxEnums.ItemType.Appointment);

    if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
      console.error("❌ Not an appointment item");
      return;
    }

    // Load session
    loadSession(loadSessionCallback);

    // Initialize organizer email
    const organizerEmail = Office.context.mailbox.userProfile.emailAddress || "";
    console.log("🎯 Setting organizer:", organizerEmail);
    setOrganizer(organizerEmail);

    // Subscribe to all available events
    console.log("📋 Starting event subscriptions...");
    subscribeToRecipientsChange();
    subscribeToTimeChange();
    subscribeToEnhancedLocations();
    subscribeToRecurrenceChange();

    // Initialize current data (async)
    await initializeCurrentData();

    console.log("✅ Event subscription initialization completed");
  }, [
    loadSession,
    loadSessionCallback,
    subscribeToRecipientsChange,
    subscribeToTimeChange,
    subscribeToEnhancedLocations,
    subscribeToRecurrenceChange,
    initializeCurrentData,
    setOrganizer,
  ]);

  useEffect(() => {
    console.log("🎯 useOutlookAppointmentEvents useEffect called!");

    // Prevent multiple subscriptions
    if (isSubscribed.current) {
      console.log("⚠️ Already subscribed, skipping...");
      return;
    }

    // Add a small delay to ensure Office.js is fully initialized
    const timeoutId = setTimeout(async () => {
      try {
        await initializeEventSubscriptions();
        isSubscribed.current = true;
      } catch (error) {
        console.error("❌ Failed to initialize event subscriptions:", error);
      }
    }, 100);

    // Cleanup function
    return () => {
      try {
        console.log("🧹 Cleaning up event subscriptions...");
        clearTimeout(timeoutId);

        // Clear all debounce timeouts
        if (recipientsDebounceRef.current) clearTimeout(recipientsDebounceRef.current);
        if (timeChangeDebounceRef.current) clearTimeout(timeChangeDebounceRef.current);

        isSubscribed.current = false;
        console.log("✅ Cleanup completed");
        // Note: Office.js doesn't provide a direct way to remove event handlers
        // The handlers will be automatically cleaned up when the add-in is unloaded
      } catch (error) {
        console.error("❌ Error during cleanup:", error);
      }
    };
  }, [initializeEventSubscriptions]);

  // Manual trigger for testing
  const triggerRecipientsCheck = useCallback(() => {
    handleRecipientsChangeInternal({} as Office.RecipientsChangedEventArgs);
  }, [handleRecipientsChangeInternal]);

  return {
    // Event subscription status
    isSubscribed: isSubscribed.current,
    eventStatus,

    // Manual test triggers
    triggerRecipientsCheck,

    // Store data getter for debugging
    getStoreData: getAppointmentStoreData,
  };
};
